import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { LOAD_CURRENT_TENANT, SET_CURRENT_TENANT } from './tenant.actions';
import { Preferences } from '@capacitor/preferences';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { TenantRepository } from '../../../shared/repositories/tenant.repository';
import { from, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { getCurrentTenant } from './tenant.selectors';
import { STORAGE_KEYS } from '../../declarations/storage-keys.enum';
import { setTag } from '@sentry/angular';
import {
  APP_TENANT_CONFIG,
  TenantConfig
} from '@bling-fe/shared/tenant-config';
import { Tenant } from '../../declarations/tenant.interface';
import { findTenantConfigByHostname } from './find-tenant-config-by-hostname';

@Injectable()
export class TenantEffects {
  private findTenantConfigByBundleId(bundleId: string): TenantConfig | null {
    return (
      this.tenantConfigs.find(
        config =>
          config.nativeApp?.bundleId === bundleId ||
          // android staging apps append .staging for staging apps
          config.nativeApp?.bundleId + '.staging' === bundleId
      ) ?? null
    );
  }

  private async resolveTenant(tenants: Tenant[]): Promise<Tenant | null> {
    // 1. Try hostname match (web)
    const hostnameConfig = findTenantConfigByHostname(
      this.tenantConfigs,
      window.location.hostname
    );
    if (hostnameConfig) {
      return (
        tenants.find(t => t.subdomain === hostnameConfig.subdomain) ?? null
      );
    }

    // 2. Try bundleId match (native)
    if (Capacitor.isNativePlatform()) {
      const appInfo = await App.getInfo();
      const bundleConfig = this.findTenantConfigByBundleId(appInfo.id);
      if (bundleConfig) {
        return (
          tenants.find(t => t.subdomain === bundleConfig.subdomain) ?? null
        );
      }
    }

    // 3. Fallback to stored tenant
    const stored = await Preferences.get({
      key: STORAGE_KEYS.CURRENT_TENANT_ID
    });
    return tenants.find(t => t.id === Number(stored.value)) ?? null;
  }

  loadCurrentTenant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_CURRENT_TENANT),
      switchMap(() =>
        this.tenantRepository.findAll().pipe(
          switchMap(tenants => from(this.resolveTenant(tenants))),
          map(t => ({ type: SET_CURRENT_TENANT, payload: t }))
        )
      ),
      catchError(() => of({ type: SET_CURRENT_TENANT, payload: null }))
    )
  );

  saveCurrentTenantToStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SET_CURRENT_TENANT),
        concatLatestFrom(() => this.store.select(getCurrentTenant)),
        switchMap(([, t]) => {
          if (t !== 'loading' && t !== null) {
            return from(
              Preferences.set({
                key: STORAGE_KEYS.CURRENT_TENANT_ID,
                value: String(t.id)
              })
            );
          } else {
            return of(t);
          }
        })
      ),
    { dispatch: false }
  );

  setTenantToSentry$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SET_CURRENT_TENANT),
        concatLatestFrom(() => this.store.select(getCurrentTenant)),
        tap(([, t]) => {
          if (t !== 'loading' && t !== null) {
            setTag('tenant', t.name);
          } else {
            setTag('tenant', null);
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private tenantRepository: TenantRepository,
    private store: Store,
    @Inject(APP_TENANT_CONFIG) private tenantConfigs: TenantConfig[]
  ) {}
}
